import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";
import {
  TelnyxErrorCode,
  TelnyxMessageDirection,
  TelnyxMessageEventType,
  TelnyxPortOutEventType,
} from "./telnyx-types";

// Define the error schema
export const telnyxErrorSchema = z.object({
  code: createEnum(TelnyxErrorCode, { enumName: "TelnyxErrorCode" }),
  title: z.string(),
  detail: z.string(),
  meta: z.object({ url: z.string() }),
  source: z.object({ parameter: z.string(), pointer: z.string() }).optional(),
});

export type TelnyxError = z.infer<typeof telnyxErrorSchema>;

// Define the media schema
export const telnyxMediaSchema = z.object({
  url: z.string(),
  content_type: z.string().nullish(),
  sha256: z.string().nullish(),
  size: z.number().nullish(),
});

export type TelnyxMedia = z.infer<typeof telnyxMediaSchema>;

// Define the message from schema
export const telnyxMessageFromSchema = z.object({
  carrier: z.string(),
  line_type: z.string(),
  phone_number: z.string(),
  status: z.string().nullish(),
});

export type TelnyxMessageFrom = z.infer<typeof telnyxMessageFromSchema>;

// Define the message to schema
export const telnyxMessageToSchema = z.object({
  carrier: z.string(),
  line_type: z.string(),
  phone_number: z.string(),
  status: z.string().nullish(),
});

export type TelnyxMessageTo = z.infer<typeof telnyxMessageToSchema>;

// Define the message payload schema
export const telnyxMessagePayloadSchema = z.object({
  completed_at: z.string().nullable(),
  cost: z
    .object({ amount: z.string().nullable(), currency: z.string().nullable() })
    .nullish(),
  cost_breakdown: z
    .object({
      carrier_fee: z.object({ amount: z.string(), currency: z.string() }),
      rate: z.object({ amount: z.string(), currency: z.string() }),
    })
    .nullish(),
  autoresponse_type: z.string().nullish(),
  direction: createEnum(TelnyxMessageDirection, {
    enumName: "TelnyxMessageDirection",
  }),
  encoding: z.string(),
  errors: z.array(telnyxErrorSchema),
  from: telnyxMessageFromSchema,
  id: z.string(),
  media: z.array(telnyxMediaSchema).nullish(),
  messaging_profile_id: z.string(),
  organization_id: z.string(),
  parts: z.number(),
  received_at: z.string(),
  record_type: z.literal("message"),
  sent_at: z.string().nullable(),
  tags: z.array(z.string()),
  text: z.string(),
  to: z.array(telnyxMessageToSchema),
  type: createEnum(["SMS", "MMS"], { enumName: "TelnyxMessageType" }),
  valid_until: z.string().nullable(),
  webhook_failover_url: z.string().nullable(),
  webhook_url: z.string(),
});

export type TelnyxMessagePayload = z.infer<typeof telnyxMessagePayloadSchema>;

// Define the message webhook schema
export const telnyxMessageWebhookSchema = z.object({
  data: z.object({
    event_type: createEnum(
      [
        TelnyxMessageEventType.MESSAGE_RECEIVED,
        TelnyxMessageEventType.MESSAGE_SENT,
        TelnyxMessageEventType.MESSAGE_FINALIZED,
      ],
      { enumName: "TelnyxMessageWebhookEventType" },
    ),
    id: z.string(),
    occurred_at: z.string(),
    payload: telnyxMessagePayloadSchema,
    record_type: z.literal("event"),
  }),
  meta: z.object({ attempt: z.number(), delivered_to: z.string() }),
});

export type TelnyxMessageWebhook = z.infer<typeof telnyxMessageWebhookSchema>;

// Port-out webhook schemas
export enum TelnyxPortOutStatus {
  PENDING = "pending",
  REJECTED_PENDING = "rejected-pending",
  REJECTED = "rejected",
  CANCELED = "canceled",
  AUTHORIZED = "authorized",
  PORTED = "ported",
}

export const telnyxPortOutPayloadSchema = z.object({
  id: z.string(), // Port-out order ID
  phone_numbers: z.array(z.string()),
  carrier_name: z.string().nullish(),
  spid: z.string().nullish(),
  rejection_reason: z.string().nullish(),
  foc_date: z.string().nullish(),
  user_id: z.string().nullish(),
  status: z.enum(TelnyxPortOutStatus),
});

export type TelnyxPortOutPayload = z.infer<typeof telnyxPortOutPayloadSchema>;

export const telnyxPortOutWebhookSchema = z.object({
  data: z.object({
    event_type: z.enum([
      TelnyxPortOutEventType.PORTOUT_STATUS_CHANGED,
      TelnyxPortOutEventType.PORTOUT_NEW_COMMENT,
      TelnyxPortOutEventType.PORTOUT_FOC_DATE_CHANGED,
    ]),
    id: z.string(),
    occurred_at: z.string(),
    payload: telnyxPortOutPayloadSchema,
    record_type: z.literal("event"),
  }),
  meta: z.object({ attempt: z.number(), delivered_to: z.string() }),
});

export type TelnyxPortOutWebhook = z.infer<typeof telnyxPortOutWebhookSchema>;

// Union type for all Telnyx webhooks
export type TelnyxWebhook = TelnyxMessageWebhook | TelnyxPortOutWebhook;
